/**
 * Mastodon followed-hashtags service.
 * Fetches and mutates the authenticated user's followed hashtags directly on their
 * instance (`/api/v1/followed_tags`, `/api/v1/tags/:name/follow|unfollow`) — same
 * authenticated, instance-local pattern as `mastodonSuggestionsService.ts`.
 */

import { ensureValidSession, parseLinkHeader, type MastodonSession } from '@/lib/mastodon'
import {
    assertSafeMastodonUrl,
    canonicalizeInstanceOrigin,
    mastodonFetchJsonWithStatus,
    UnsafeUrlError,
} from '@/lib/mastodonFetch'
import {
    normalizeMastodonTagName,
    isValidMastodonTagName,
    type MastodonFollowedTag,
} from '@/lib/mastodonTag'

export type { MastodonFollowedTag } from '@/lib/mastodonTag'

/**
 * Page cap for `GET /api/v1/followed_tags` Link-header pagination. This is a *page*
 * count, not an item count: `limit=200` is a request hint the server may honour with
 * fewer items, so the cap trips at the 5th Link-driven page whatever those pages
 * contain — 1,000 tags is the best-case ceiling, not a guarantee.
 */
export const MAX_FOLLOWED_TAG_PAGES = 5

/**
 * Split `str` on `separator`, ignoring any separator occurrence inside a
 * double-quoted region (unescaped `"` toggles quote state). RFC 8288 param
 * values may contain `;`/`,` inside a quoted-string, so a plain `String.split`
 * on those characters mis-parses a value like `title="foo; rel=next ; bar"` as
 * additional parameters instead of one quoted string.
 */
function splitOutsideQuotes(str: string, separator: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < str.length; i++) {
        const ch = str[i]
        if (ch === '"' && str[i - 1] !== '\\') inQuotes = !inQuotes
        if (ch === separator && !inQuotes) {
            result.push(current)
            current = ''
        } else {
            current += ch
        }
    }
    result.push(current)
    return result
}

/**
 * Loose "does this Link header declare a `next` relation at all" detector, used only
 * as a tie-breaker when the shared, strict `parseLinkHeader` (which matches exactly one
 * literal `<url>; rel="next"` form) returns undefined. Relation-token aware on purpose:
 * it splits each link-value's `rel` parameter value on whitespace and requires an exact
 * `next` token, so an extension relation URI such as `rel="https://example.test/next"`,
 * a `next` inside the URI's query string, or a `title="next"` parameter never count.
 * Comma/semicolon splitting is quoted-string-aware (via `splitOutsideQuotes`), so a `;`
 * or `,` inside a quoted parameter value (e.g. `title="foo; rel=next ; bar"`) is never
 * mistaken for a parameter or link-value separator; not a full RFC 8288 parser otherwise.
 */
function linkHeaderDeclaresNext(header: string | null): boolean {
    if (!header) return false
    for (const linkValue of splitOutsideQuotes(header, ',')) {
        for (const param of splitOutsideQuotes(linkValue, ';').slice(1)) {
            const eq = param.indexOf('=')
            if (eq === -1) continue
            if (param.slice(0, eq).trim().toLowerCase() !== 'rel') continue
            const raw = param
                .slice(eq + 1)
                .trim()
                .replace(/^"([\s\S]*)"$/, '$1')
            if (raw.split(/\s+/).some(token => token.toLowerCase() === 'next')) return true
        }
    }
    return false
}

/**
 * Fetch the authenticated user's followed hashtags, paginating via the Link `next`
 * relation up to `MAX_FOLLOWED_TAG_PAGES` pages.
 *
 * Divergence from every other cursor-safety helper in this codebase (webapp and
 * mobile): here, an unsafe or unparseable `next` THROWS instead of silently ending
 * pagination. `resolveNextUrl` in `lib/mastodon.ts` and the timeline/list fetchers drop
 * an off-origin or malformed Link cursor and just stop — correct for a post timeline,
 * where "no more posts to show" is a fine outcome. It is wrong here: a silent stop on
 * any page would return a partial followed-tags set that `useMastodonFollowedTags`
 * then treats as the complete, authoritative list, and the next user tab mutation would
 * prune the stored `mastodon-hashtag:` entries for every tag past the drop point.
 *
 * Both throw classes below (unsafe origin, and a declared-but-unparseable `next`) are
 * `UnsafeUrlError` on purpose: `shouldRetryQuery` (lib/mastodonFetch.ts) never retries
 * that class, because a cursor that failed validation is not going to become safe or
 * parseable on a blind retry. Recovery is the next fetch trigger — a new consumer
 * mount, a manual `refetch`, or the server fixing its header — never an automatic retry.
 *
 * A first-page HTTP 404 is treated as an authoritative empty result (some
 * servers/forks don't implement this endpoint). A 404 on a later page is a genuine
 * failure and throws, as does any other non-ok status or a non-array body.
 */
export async function fetchMastodonFollowedTags(
    rawSession: MastodonSession
): Promise<MastodonFollowedTag[]> {
    const session = await ensureValidSession(rawSession)
    const { origin } = canonicalizeInstanceOrigin(session.instance_url)

    const firstUrl = new URL('/api/v1/followed_tags', origin)
    firstUrl.searchParams.set('limit', '200')

    const results: MastodonFollowedTag[] = []
    const seen = new Set<string>()
    let currentUrl = firstUrl.toString()
    let page = 0

    for (;;) {
        const result = await mastodonFetchJsonWithStatus<unknown>(
            currentUrl,
            { headers: { Authorization: `Bearer ${session.access_token}` } },
            { expectedOrigin: origin }
        )

        if (!result.ok) {
            if (page === 0 && result.status === 404) return []
            throw new Error(`Mastodon followed tags fetch failed: HTTP ${result.status}`)
        }

        const data = result.data
        if (!Array.isArray(data)) {
            throw new Error('Invalid response from Mastodon followed tags')
        }

        for (const entry of data) {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
            const name = (entry as Record<string, unknown>).name
            if (typeof name !== 'string') continue
            const normalized = normalizeMastodonTagName(name)
            // A server-returned name that fails the same charset check used at the
            // write boundary (isValidMastodonTagName) would otherwise surface as a
            // Home tab the adapter refuses to dispatch — a broken, unclickable tab.
            // Skip it here so the fetched set only ever contains names the rest of
            // the pipeline can actually act on.
            if (!normalized || seen.has(normalized) || !isValidMastodonTagName(normalized)) continue
            seen.add(normalized)
            results.push({ name: name.trim() })
        }

        page += 1

        const next = parseLinkHeader(result.linkHeader)
        if (!next) {
            if (linkHeaderDeclaresNext(result.linkHeader)) {
                throw new UnsafeUrlError(
                    'Mastodon followed tags: Link header declares a next page that could not be parsed'
                )
            }
            return results
        }

        if (page >= MAX_FOLLOWED_TAG_PAGES) {
            throw new Error(`Mastodon followed tags exceeded ${MAX_FOLLOWED_TAG_PAGES} pages`)
        }

        assertSafeMastodonUrl(next, origin)
        currentUrl = next
    }
}

function parseTagResponse(data: unknown, op: 'follow' | 'unfollow'): MastodonFollowedTag {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error(`Mastodon ${op} tag: invalid response`)
    }
    const name = (data as Record<string, unknown>).name
    if (typeof name !== 'string' || normalizeMastodonTagName(name) === '') {
        throw new Error(`Mastodon ${op} tag: invalid response`)
    }
    return { name: name.trim() }
}

async function postTagAction(
    rawSession: MastodonSession,
    name: string,
    op: 'follow' | 'unfollow'
): Promise<MastodonFollowedTag> {
    const session = await ensureValidSession(rawSession)
    const { origin } = canonicalizeInstanceOrigin(session.instance_url)
    const url = new URL(`/api/v1/tags/${encodeURIComponent(name)}/${op}`, origin)

    const result = await mastodonFetchJsonWithStatus<unknown>(
        url.toString(),
        { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` } },
        { expectedOrigin: origin }
    )

    if (!result.ok) {
        throw new Error(`Mastodon ${op} tag failed: HTTP ${result.status}`)
    }
    return parseTagResponse(result.data, op)
}

/** Follow a hashtag on the user's home instance. `name` should already be normalised. */
export function followMastodonTag(
    rawSession: MastodonSession,
    name: string
): Promise<MastodonFollowedTag> {
    return postTagAction(rawSession, name, 'follow')
}

/** Unfollow a hashtag on the user's home instance. `name` should already be normalised. */
export function unfollowMastodonTag(
    rawSession: MastodonSession,
    name: string
): Promise<MastodonFollowedTag> {
    return postTagAction(rawSession, name, 'unfollow')
}
